/**
 * Roadmap-matrix-configuratie (roadmap-pack, view-kind `roadmap.roadmap-matrix`).
 * Twee keuzes: het Program (= Roadmap → bepaalt de plateau-kolommen) en het
 * concept-type/domein (= welke elementen droppable zijn). Verbergt zichzelf bij
 * andere view-kinds. Spiegelt `HeatMapConfigBlock`.
 *
 * Gedeeld zodat zowel het Diagrammen-paneel als het Explorer-diagram-detail 'm
 * naast hun Type-dropdown kunnen tonen.
 */
import type { BilingualText } from "@ae/domain";
import { useTranslation } from "react-i18next";
import { getRegistry, resolveBilingual } from "../../registry";
import { useWorkspaceData } from "../../state/workspace";
import { readRoadmapMatrixConfig } from "../canvas/roadmapMatrixConfig";

/** Concept-type/domein-keuzes voor de Roadmap-matrix (roadmap-tijdreizen.md
 *  §6.4). De `key` wordt opgeslagen als `conceptFilter`; de render-pad bepaalt
 *  in chunk 4 welke element-types per domein droppable zijn. */
export const MATRIX_DOMAINS: { key: string; label: BilingualText }[] = [
  { key: "bmc", label: { nl: "BMC", en: "BMC" } },
  { key: "sharp", label: { nl: "Sharp-concepten", en: "Sharp concepts" } },
  { key: "c4", label: { nl: "C4", en: "C4" } },
  { key: "archimate", label: { nl: "ArchiMate", en: "ArchiMate" } },
  { key: "risk", label: { nl: "Risk & Security", en: "Risk & Security" } },
];

export function RoadmapMatrixConfigBlock({
  diagramId,
  workspaceId,
  branchId,
  lang,
}: {
  diagramId: string;
  workspaceId: string;
  branchId: string;
  lang: string;
}) {
  const { t } = useTranslation();
  const reg = getRegistry();
  // Hook vóór de early-return (rules-of-hooks). `revision` triggert her-lezen
  // van diagram + Program-lijst na een store-mutatie.
  const revision = useWorkspaceData((s) => s.revision);
  void revision;
  // Volledige diagram (incl. viewState) uit de store — de Explorer geeft alleen
  // een licht diagram-item door, dus we resolven hier op id.
  const diagram = useWorkspaceData.getState().store.getDiagram(diagramId);
  if (!diagram) return null;
  const vk = reg.getViewKind(diagram.viewKind);
  if (vk?.layout_mode !== "roadmap-matrix") return null;

  const config = readRoadmapMatrixConfig(diagram.viewState);
  const programs = useWorkspaceData
    .getState()
    .store.listConcepts()
    .filter((c) => c.typeId === "archimate.Program" && !c.tombstoneInBranch);

  const update = (next: import("@ae/domain").RoadmapMatrixConfig) => {
    useWorkspaceData.getState().setRoadmapMatrixConfig(workspaceId, branchId, diagramId, next);
  };

  return (
    <div className="mt-3 rounded border border-slate-200 bg-slate-50 px-2 py-2">
      <div className="font-semibold text-[10px] text-slate-600 uppercase tracking-wide">
        {t("roadmapMatrix.configTitle")}
      </div>

      <label className="mt-2 block text-[10px] text-slate-500 uppercase tracking-wide">
        {t("roadmapMatrix.programLabel")}
      </label>
      <select
        className="mt-0.5 w-full rounded border border-slate-300 bg-white px-1 py-0.5 text-xs focus:border-blue-500 focus:outline-none"
        value={config.programId ?? ""}
        onChange={(e) => {
          update({ ...config, programId: e.target.value || undefined });
        }}
      >
        <option value="">{t("roadmapMatrix.programChoose")}</option>
        {programs.map((p) => (
          <option key={p.id} value={p.id}>
            {resolveBilingual(p.name, lang) || p.id}
          </option>
        ))}
      </select>
      {programs.length === 0 && (
        <div className="mt-1 text-[11px] text-amber-700 italic">
          {t("roadmapMatrix.programNone")}
        </div>
      )}

      <label className="mt-2 block text-[10px] text-slate-500 uppercase tracking-wide">
        {t("roadmapMatrix.domainLabel")}
      </label>
      <select
        className="mt-0.5 w-full rounded border border-slate-300 bg-white px-1 py-0.5 text-xs focus:border-blue-500 focus:outline-none"
        value={config.conceptFilter ?? ""}
        onChange={(e) => {
          update({ ...config, conceptFilter: e.target.value || undefined });
        }}
      >
        <option value="">{t("roadmapMatrix.domainChoose")}</option>
        {MATRIX_DOMAINS.map((d) => (
          <option key={d.key} value={d.key}>
            {resolveBilingual(d.label, lang)}
          </option>
        ))}
      </select>
    </div>
  );
}
